import React, { useEffect, useState } from "react";
import Button from "components/Button";
import Checkbox from "components/Checkbox";
import DesktopContainerAuth from "components/DesktopContainerAuth";
import Label from "components/Label";
import Link from "components/Link";
import Loading from "components/Loading";
import TextInput from "components/TextInput";
import ContactAuth from "components/Views/ContactAuth";
import TermOfUse from "components/Views/TermOfUse";
import handleErrors from "helpers/handleErrors";
import { login } from "services/auth";
import { schemaLogin } from "./validation";
import { useHistory } from "react-router-dom";
import PasswordInput from "components/PasswordInput";
import { useAuth } from "context/auth";
import toastMessage from "helpers/handleToastMessage";
import { ToastContainer } from "react-toastify";
import jwt_decode from "jwt-decode";

type Props = {};

const Login: React.FC<Props> = (): JSX.Element => {
  let history = useHistory();

  const { setUserName, setUserRole } = useAuth();

  const [role, setRole] = useState<string>("");
  useEffect(() => {
    localStorage.clear();
    setRole(history.location.pathname.split("/")[2]);
    localStorage.setItem("role", role);
  }, [history, role]);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>();

  const addClassButton = () => {
    if (errors?.email || errors?.password) {
      return "bg-bt-red text-bt-white";
    }

    if (email && password) {
      return "bg-bt-green text-bt-white";
    }

    return "bg-bt-gray text-bt-white";
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (!schemaLogin.isValidSync({ email, password, role })) {
        handleErrors({
          schema: schemaLogin,
          stateData: { email, password, role },
          setErrorMessage: setErrors,
        });
        setLoading(false);
        return;
      }

      const { data } = await login({ email, password, role });
      if (data) {
        if (rememberMe) {
          localStorage.setItem("rememberMe", JSON.stringify(true));
          localStorage.setItem("email", email);
          localStorage.setItem("password", password);
        }
        localStorage.setItem("token", JSON.stringify(data.accessToken));
        let decoded: any = jwt_decode(data.accessToken);
        setUserName(JSON.stringify(decoded.user?.name));
        setUserRole(JSON.stringify(decoded.user?.role));

        toastMessage({
          message: "Login successfully.",
          type: "success",
        });

        if (role === "admin") {
          history.push("/entity-management");
        } else {
          history.push("/manage-families");
        }

        window.location.reload();
      }
    } catch (error: any) {
      let message;

      if (error.response.status === 406) {
        toastMessage({
          message: "You will be redirected to create the password.",
          type: "success",
        });

        history.push(`/new-password/${role}`, {
          state: error.response.data.state,
          email,
          role,
        });
      }
      if (error.response.status === 403) {
        message =
          "Your email has not yet been activated. Try again later, and if the error persists, please contact our team.";
        setErrors({
          email:
            "Your email has not yet been activated. Try again later, and if the error persists, please contact our team.",
        });
      }
      if (error.response.data.error === "Invalid username and/or password.") {
        message = "Invalid username and/or password.";
        setErrors({
          email: error.response.data.error,
          password: error.response.data.error,
        });
      }

      toastMessage({
        message: message || "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DesktopContainerAuth>
      <Loading loading={loading} />
      <div className="flex flex-col">
        <div className="w-96 mx-auto">
          <Label
            label={"Login"}
            className="font-light text-2xl text-bt-purple"
          />
          <Label
            label={"Welcome back! Enter your credentials:"}
            className="mt-4 font-light text-base"
            style={{
              color: "#4D4F5C",
              opacity: 0.5,
            }}
          />
          <TextInput
            placeholder={"Email"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            isHelp={errors?.email}
            helpText={errors?.email}
          />
          <PasswordInput
            placeholder={"Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            isHelp={errors?.password}
            helpText={errors?.password}
          />
          <div className="flex flex-row my-2 justify-between">
            <Checkbox
              label={"Remember me"}
              defaultChecked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              checkboxStyle="rounded-sm focus:ring-0 checked:bg-bt-green"
              className="font-light text-sm text-bt-purple"
            />
            <Link
              label={"Forgot password"}
              className="font-light text-sm text-bt-purple"
              href={`/forgot-password/${role}`}
            />
          </div>
          <Button
            label={"Login"}
            className={`mt-12 h-8 w-full rounded-lg font-sora font-light text-white ${addClassButton()}`}
            onClick={handleSubmit}
          />
        </div>
        <ContactAuth
          title={"Request your access"}
          subTitle={
            "Are you an adoption professional and feel this would be the perfect solution for your business?"
          }
          titleButton={"Contact form"}
          action={"/signup/entity"}
        />
        <TermOfUse />
      </div>
      <ToastContainer />
    </DesktopContainerAuth>
  );
};

export default Login;
